import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MeasuringStrategy,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import type { DragEndEvent, DragMoveEvent, DragOverEvent, DragStartEvent } from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import type { AnimateLayoutChanges } from '@dnd-kit/sortable'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faGripVertical,
  faLayerGroup,
  faInfoCircle,
  faAsterisk,
  faDatabase,
  faBan,
  faFont,
  faSquareCheck,
  faCircleDot,
  faList,
  faSignature,
  faChevronUp,
  faChevronDown,
  faOutdent,
  faIndent,
} from '@fortawesome/free-solid-svg-icons'
import {
  TRAY_ID,
  fieldId,
  findItemLocation,
  flattenVisible,
  getTreeProjection,
  indentItem,
  moveItem,
  moveSibling,
  outdentItem,
  persistPages,
  projectionToMove,
  sanitizeTree,
  type FieldGroup as StateFieldGroup,
  type FieldGroupChild as StateFieldGroupChild,
  type FlatRow,
  type PageDef as StatePageDef,
  type TreeState,
} from './pdfFieldsTreeState'

export type PdfField = {
  name: string
  acroName?: string
  type: string
  options?: string[]
  rect?: { x: number; y: number; width: number; height: number }
  page?: number
  widgets?: Array<{
    widgetId: string
    rect?: { x: number; y: number; width: number; height: number }
    page?: number
  }>
  instanceCount?: number
}
export type FieldGroupChild = StateFieldGroupChild
export type FieldGroup = StateFieldGroup
export type PageDef = StatePageDef
export type FieldConfig = {
  label: string
  type: string
  required: boolean
  excludeFromCache?: boolean
}
export type PdfFieldTreeData = {
  kind: 'page' | 'group' | 'field'
  pageId?: string
  groupId?: string
  fieldName?: string
  label: string
  value?: string
  fieldType?: string
  required?: boolean
  hasPosition?: boolean
  cacheEnabled?: boolean
  excludeFromCache?: boolean
}

const INDENTATION_WIDTH = 24

const measuring = {
  droppable: {
    strategy: MeasuringStrategy.Always,
  },
}

const animateLayoutChanges: AnimateLayoutChanges = ({ isSorting, wasDragging }) =>
  isSorting || wasDragging ? false : true

function getPdfTypeIcon(t: string) {
  if (t === 'checkbox') return faSquareCheck
  if (t === 'radio') return faCircleDot
  if (t === 'select') return faList
  if (t === 'signature') return faSignature
  return faFont
}

type Kind = 'page' | 'group' | 'field' | 'tray'

type RowModel = {
  id: string
  kind: Kind
  label: string
  depth: number
  fieldName?: string
  fieldType?: string
  required?: boolean
  hasPosition?: boolean
  hasChildren: boolean
  descendantCount: number
  canDrag: boolean
}

type SharedRowProps = {
  cacheEnabled: boolean
  fieldConfigs: Record<string, FieldConfig>
  selectedKeys: string[]
  collapsedIds: Set<string>
  onItemClick: (e: React.MouseEvent, id: string) => void
  onCollapse: (id: string) => void
  onMove: (id: string, direction: -1 | 1) => void
  onIndent: (id: string) => void
  onOutdent: (id: string) => void
  fieldListRefs: React.MutableRefObject<Map<string, HTMLElement>>
}

function TreeRow({
  row,
  isOverlay,
  isDragging,
  dropInto,
  dropLineDepth,
  dragListeners,
  dragAttributes,
  innerRef,
  ...shared
}: SharedRowProps & {
  row: RowModel
  isOverlay?: boolean
  isDragging?: boolean
  dropInto?: boolean
  dropLineDepth?: number | null
  dragListeners?: ReturnType<typeof useSortable>['listeners']
  dragAttributes?: ReturnType<typeof useSortable>['attributes']
  innerRef?: (node: HTMLElement | null) => void
}) {
  const selected = shared.selectedKeys.includes(row.id)
  const collapsed = shared.collapsedIds.has(row.id) || !!isOverlay || !!isDragging
  const showActions = !isOverlay && row.kind !== 'tray'
  return (
    <div
      ref={innerRef}
      className={`relative select-none ${isDragging && !isOverlay ? 'opacity-40' : ''} ${
        row.kind === 'tray' && !isOverlay ? 'mt-2 pt-2 border-t border-base-300' : ''
      }`}
      style={{ paddingLeft: `${row.depth * INDENTATION_WIDTH + 4}px` }}
    >
      {dropLineDepth != null && (
        <div
          className="pointer-events-none absolute z-20 flex items-center"
          style={{ top: 0, left: dropLineDepth * INDENTATION_WIDTH, right: 8 }}
        >
          <span className="h-2 w-2 shrink-0 rounded-full bg-primary" />
          <span className="h-0.5 flex-1 rounded-full bg-primary" />
        </div>
      )}

      <div
        className={`group flex items-center gap-1.5 w-full min-w-0 py-1 px-1.5 rounded cursor-pointer ${
          isOverlay
            ? 'bg-base-100 shadow-xl ring-2 ring-primary/60'
            : dropInto
              ? 'bg-primary/10 ring-2 ring-primary/50'
              : selected
                ? 'bg-primary/10 ring-1 ring-primary/30'
                : 'hover:bg-base-200'
        }`}
        onClick={e => {
          if (!isOverlay && row.kind !== 'tray') shared.onItemClick(e, row.id)
        }}
        role="button"
        tabIndex={0}
        ref={el => {
          if (row.kind === 'field' && row.fieldName && el) shared.fieldListRefs.current.set(row.fieldName, el)
        }}
      >
        {row.hasChildren ? (
          <button
            type="button"
            className="btn btn-ghost btn-xs shrink-0 p-0.5 min-h-0 h-5 w-5"
            onClick={e => {
              e.stopPropagation()
              if (!isOverlay) shared.onCollapse(row.id)
            }}
          >
            <span className="text-xs leading-none">{collapsed ? '▸' : '▾'}</span>
          </button>
        ) : (
          <span className="w-5 shrink-0" />
        )}

        {row.canDrag ? (
          <span
            {...dragListeners}
            {...dragAttributes}
            className="cursor-grab active:cursor-grabbing text-base-content/25 hover:text-base-content/50 shrink-0 touch-none"
            onClick={e => e.stopPropagation()}
          >
            <FontAwesomeIcon icon={faGripVertical} className="text-xs" />
          </span>
        ) : (
          <span className="w-3 shrink-0" />
        )}

        {row.kind === 'page' && (
          <span className="font-semibold truncate text-sm flex-1">{row.label}</span>
        )}
        {row.kind === 'group' && (
          <>
            <FontAwesomeIcon icon={faLayerGroup} className="opacity-60 shrink-0 text-xs" />
            <span className="truncate font-medium text-sm flex-1">{row.label}</span>
          </>
        )}
        {row.kind === 'tray' && (
          <span className="flex-1 truncate text-xs font-semibold uppercase tracking-wide text-base-content/60">
            {row.label}
          </span>
        )}
        {row.kind === 'field' && (
          <span className={`truncate text-sm flex-1 ${!row.hasPosition ? 'opacity-60' : ''}`}>
            {!row.hasPosition && (
              <FontAwesomeIcon icon={faInfoCircle} className="text-warning mr-1 text-xs" title="Position not available" />
            )}
            {row.label}
          </span>
        )}

        {isOverlay && row.descendantCount > 0 && (
          <span className="badge badge-primary badge-sm shrink-0" title="Nested items moving with this row">
            {row.descendantCount}
          </span>
        )}

        {showActions && (
          <div className="flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 focus-within:opacity-100">
            {row.kind !== 'page' && (
              <>
                <button type="button" className="btn btn-ghost btn-xs min-h-0 h-5 w-5 p-0" title="Outdent" onClick={e => { e.stopPropagation(); shared.onOutdent(row.id) }}>
                  <FontAwesomeIcon icon={faOutdent} className="text-[10px]" />
                </button>
                <button type="button" className="btn btn-ghost btn-xs min-h-0 h-5 w-5 p-0" title="Indent into previous group" onClick={e => { e.stopPropagation(); shared.onIndent(row.id) }}>
                  <FontAwesomeIcon icon={faIndent} className="text-[10px]" />
                </button>
              </>
            )}
            <button type="button" className="btn btn-ghost btn-xs min-h-0 h-5 w-5 p-0" title="Move up" onClick={e => { e.stopPropagation(); shared.onMove(row.id, -1) }}>
              <FontAwesomeIcon icon={faChevronUp} className="text-[10px]" />
            </button>
            <button type="button" className="btn btn-ghost btn-xs min-h-0 h-5 w-5 p-0" title="Move down" onClick={e => { e.stopPropagation(); shared.onMove(row.id, 1) }}>
              <FontAwesomeIcon icon={faChevronDown} className="text-[10px]" />
            </button>
          </div>
        )}

        {row.kind === 'field' && (
          <div className="flex items-center gap-1 shrink-0">
            {row.required !== false && (
              <FontAwesomeIcon icon={faAsterisk} className="text-error text-xs" title="Required" />
            )}
            {shared.cacheEnabled && row.fieldName && !shared.fieldConfigs[row.fieldName]?.excludeFromCache && (
              <FontAwesomeIcon icon={faDatabase} className="text-success text-xs" title="Cache enabled" />
            )}
            {shared.cacheEnabled && row.fieldName && shared.fieldConfigs[row.fieldName]?.excludeFromCache && (
              <FontAwesomeIcon icon={faBan} className="text-base-content/40 text-xs" title="Excluded from cache" />
            )}
            {row.fieldType && (
              <FontAwesomeIcon icon={getPdfTypeIcon(row.fieldType)} className="text-base-content/50 text-xs" title={row.fieldType} />
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function SortableRow(props: SharedRowProps & {
  row: RowModel
  dropInto?: boolean
  dropLineDepth?: number | null
}) {
  const { setNodeRef, attributes, listeners, isDragging } = useSortable({
    id: props.row.id,
    disabled: !props.row.canDrag,
    animateLayoutChanges,
  })
  return (
    <TreeRow
      {...props}
      innerRef={setNodeRef}
      isDragging={isDragging}
      dragListeners={listeners}
      dragAttributes={attributes}
    />
  )
}

function fieldRow(
  name: string,
  depth: number,
  parsedPdfFields: PdfField[],
  fieldConfigs: Record<string, FieldConfig>,
): RowModel {
  const f = parsedPdfFields.find(x => x.name === name)
  const cfg = fieldConfigs[name]
  const raw = cfg?.label || name
  const label = raw.includes('undefined') ? name : raw
  const hasWidgetPosition = !!f?.widgets?.some(w => !!w?.rect)
  return {
    id: fieldId(name),
    kind: 'field',
    label,
    depth,
    fieldName: name,
    fieldType: f?.type,
    required: cfg?.required !== false,
    hasPosition: !!f?.rect || hasWidgetPosition,
    hasChildren: false,
    descendantCount: 0,
    canDrag: true,
  }
}

function toRowModel(
  row: FlatRow,
  parsedPdfFields: PdfField[],
  fieldConfigs: Record<string, FieldConfig>,
): RowModel {
  if (row.kind === 'field' && row.fieldName) {
    return fieldRow(row.fieldName, row.depth, parsedPdfFields, fieldConfigs)
  }
  return {
    id: row.id,
    kind: row.kind,
    label: row.label,
    depth: row.depth,
    hasChildren: row.hasChildren,
    descendantCount: row.descendantCount,
    canDrag: row.kind !== 'tray',
  }
}

export type PdfFieldsTreeProps = {
  pages: PageDef[]
  fieldGroups: FieldGroup[]
  setPages: (updater: (prev: PageDef[]) => PageDef[]) => void
  setFieldGroups: (updater: (prev: FieldGroup[]) => FieldGroup[]) => void
  parsedPdfFields: PdfField[]
  fieldConfigs: Record<string, FieldConfig>
  managementDetails: { cache?: { enabled?: boolean } }
  selectedKeys: string[]
  onItemClick: (e: React.MouseEvent, key: string) => void
  fieldListRefs: React.MutableRefObject<Map<string, HTMLElement>>
}

export const PdfFieldsTree: React.FC<PdfFieldsTreeProps> = ({
  pages,
  fieldGroups,
  setPages,
  setFieldGroups,
  parsedPdfFields,
  fieldConfigs,
  managementDetails,
  selectedKeys,
  onItemClick,
  fieldListRefs,
}) => {
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set())
  const [activeId, setActiveId] = useState<string | null>(null)
  const [overId, setOverId] = useState<string | null>(null)
  const [offsetLeft, setOffsetLeft] = useState(0)

  const parsedNames = useMemo(() => parsedPdfFields.map(f => f.name), [parsedPdfFields])
  const tree: TreeState = useMemo(
    () => sanitizeTree({ pages, fieldGroups }, parsedNames),
    [pages, fieldGroups, parsedNames],
  )

  useEffect(() => {
    if (parsedNames.length === 0) return
    if (pages.some(p => p.id !== 'unassigned')) return
    const nextPages = persistPages({ pages, fieldGroups }, parsedNames)
    setPages(() => nextPages)
  }, [parsedNames, pages, fieldGroups, setPages])

  const apply = useCallback((next: TreeState) => {
    setPages(() => next.pages)
    setFieldGroups(() => next.fieldGroups)
  }, [setPages, setFieldGroups])

  const visibleRows = useMemo(
    () => flattenVisible(tree, parsedNames, collapsedIds, activeId),
    [tree, parsedNames, collapsedIds, activeId],
  )
  const rowModels = useMemo(
    () => visibleRows.map(row => toRowModel(row, parsedPdfFields, fieldConfigs)),
    [visibleRows, parsedPdfFields, fieldConfigs],
  )
  const sortedIds = useMemo(() => visibleRows.map(row => row.id), [visibleRows])

  const projected = activeId && overId
    ? getTreeProjection(visibleRows, activeId, overId, offsetLeft, INDENTATION_WIDTH)
    : null

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const cacheEnabled = !!managementDetails.cache?.enabled
  const shared: SharedRowProps = {
    cacheEnabled,
    fieldConfigs,
    selectedKeys,
    collapsedIds,
    onItemClick,
    onCollapse: id => {
      setCollapsedIds(prev => {
        const next = new Set(prev)
        if (next.has(id)) next.delete(id)
        else next.add(id)
        return next
      })
    },
    onMove: (id, direction) => apply(moveSibling(tree, id, direction, parsedNames)),
    onIndent: id => apply(indentItem(tree, id, parsedNames)),
    onOutdent: id => apply(outdentItem(tree, id, parsedNames)),
    fieldListRefs,
  }

  const resetDrag = useCallback(() => {
    setActiveId(null)
    setOverId(null)
    setOffsetLeft(0)
  }, [])

  const handleDragStart = useCallback(({ active }: DragStartEvent) => {
    const id = String(active.id)
    setActiveId(id)
    setOverId(id)
  }, [])

  const handleDragMove = useCallback(({ delta }: DragMoveEvent) => {
    setOffsetLeft(delta.x)
  }, [])

  const handleDragOver = useCallback(({ over }: DragOverEvent) => {
    setOverId(over ? String(over.id) : null)
  }, [])

  const handleDragEnd = useCallback(({ active, over, delta }: DragEndEvent) => {
    const currentActive = String(active.id)
    const currentOver = over ? String(over.id) : null
    const rows = flattenVisible(tree, parsedNames, collapsedIds, currentActive)
    resetDrag()
    if (!currentOver) return
    const nextProjected = getTreeProjection(rows, currentActive, currentOver, delta.x, INDENTATION_WIDTH)
    if (!nextProjected) return
    const drop = projectionToMove(rows, currentActive, currentOver, nextProjected)
    if (!drop) return
    const loc = findItemLocation(tree, currentActive, parsedNames)
    if (loc && loc.parent === drop.parent && loc.index === drop.index) return
    apply(moveItem(tree, currentActive, drop.parent, drop.index, parsedNames))
  }, [tree, parsedNames, collapsedIds, apply, resetDrag])

  const activeRow = useMemo(() => {
    if (!activeId) return null
    const row = visibleRows.find(r => r.id === activeId)
    if (!row) return null
    return toRowModel(row, parsedPdfFields, fieldConfigs)
  }, [activeId, visibleRows, parsedPdfFields, fieldConfigs])

  if (parsedPdfFields.length === 0) {
    return (
      <div className="px-2 py-1 text-sm text-base-content/60">
        No fields found. Upload a fillable PDF.
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-0.5">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        measuring={measuring}
        onDragStart={handleDragStart}
        onDragMove={handleDragMove}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={resetDrag}
      >
        <SortableContext items={sortedIds} strategy={verticalListSortingStrategy}>
          {rowModels.map(row => {
            const isOver = overId === row.id && !!activeId
            const nestingInto = !!(projected && isOver && projected.parentId === row.id)
            return (
              <SortableRow
                key={row.id}
                {...shared}
                row={row}
                dropInto={nestingInto}
                dropLineDepth={isOver && projected && !nestingInto ? projected.depth : null}
              />
            )
          })}
        </SortableContext>

        <DragOverlay dropAnimation={null}>
          {activeRow ? <TreeRow {...shared} row={activeRow} isOverlay /> : null}
        </DragOverlay>
      </DndContext>
    </div>
  )
}
